/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { Beneficiary } from '~/types';
import { beneficiaries } from '~/routes';
import {
  getBeneficiaries,
  createBeneficiary,
  deleteBeneficiary,
  updateBeneficiary,
} from '~/api/beneficiaries';

type UseMutationType = typeof useMutation;
type UseMutationParams = Parameters<UseMutationType>;

export const useBeneficiariesQuery = (options?: {
  onSuccess?: (res: Beneficiary[]) => void;
  enabled?: boolean;
}) => {
  return useQuery<Beneficiary[]>(
    beneficiaries,
    () => getBeneficiaries({ page: 1, perPage: 9999 }).then((res) => res.data),
    options
  );
};

export const useBeneficiariesEdit = (options?: UseMutationParams[2]) => {
  const queryClient = useQueryClient();
  const [t] = useTranslation();
  const key = beneficiaries;

  return useMutation((body: Beneficiary) => updateBeneficiary(handleBody(body)), {
    onMutate(newData) {
      const resultNewData = handleBody(newData);
      if (options?.onMutate) options.onMutate(resultNewData);
      const prevData = queryClient.getQueryData<Beneficiary[]>(key);
      const rollback = () => {
        queryClient.setQueryData(key, prevData);
      };

      queryClient.setQueryData<Beneficiary[] | undefined>(key, (prev) => {
        if (prev) {
          return prev.map((item) => {
            if (item.id === resultNewData.id) return resultNewData;
            return item;
          });
        }
        return prev;
      });

      void message.loading({ content: t('updating'), key });
      const successMessage = () => {
        void message.success({ content: t('updated'), key, duration: 2 });
      };
      const errorMessage = (content: string) => {
        void message.error({ content, key, duration: 2 });
      };

      return {
        rollback,
        successMessage,
        errorMessage,
      };
    },
    onSuccess(res, newData, contenxt: any) {
      if (options?.onSuccess) void options.onSuccess(res, newData, contenxt);
      contenxt.successMessage();
      void queryClient.invalidateQueries(key);
    },
    onError(error: any, newData, contenxt) {
      if (options?.onError) void options.onError(error, newData, contenxt);
      void contenxt.errorMessage(t(error?.response?.data?.message) || 'Error!');
      contenxt.rollback();
    },
  });
};

export const useBeneficiariesCreate = (options?: UseMutationParams[2]) => {
  const queryClient = useQueryClient();
  const [t] = useTranslation();
  const key = beneficiaries;

  return useMutation((body: Beneficiary) => createBeneficiary(handleBody(body)), {
    onMutate(newData) {
      const resultNewData = handleBody(newData);
      if (options?.onMutate) options.onMutate(resultNewData);
      const prevData = queryClient.getQueryData<Beneficiary[]>(key);
      const rollback = () => {
        queryClient.setQueryData(key, prevData);
      };

      queryClient.setQueryData<Beneficiary[] | undefined>(key, (prev) => {
        if (prev) {
          return prev.concat([resultNewData]);
        }
        return prev;
      });

      void message.loading({ content: t('creating'), key });
      const successMessage = () => {
        void message.success({ content: t('created'), key, duration: 2 });
      };
      const errorMessage = (content: string) => {
        void message.error({ content, key, duration: 2 });
      };

      return {
        rollback,
        successMessage,
        errorMessage,
      };
    },
    onSuccess(res, newData, contenxt: any) {
      if (options?.onSuccess) void options.onSuccess(res, newData, contenxt);
      contenxt.successMessage();
      void queryClient.invalidateQueries(key);
    },
    onError(error: any, newData, contenxt) {
      if (options?.onError) void options.onError(error, newData, contenxt);
      void contenxt.errorMessage(t(error?.response?.data?.message) || 'Error!');
      contenxt.rollback();
    },
  });
};

export const useBeneficiariesDelete = (options?: UseMutationParams[2]) => {
  const queryClient = useQueryClient();
  const [t] = useTranslation();
  const key = beneficiaries;

  return useMutation(deleteBeneficiary, {
    onMutate(id) {
      if (options?.onMutate) options.onMutate(id);
      const prevData = queryClient.getQueryData<Beneficiary[]>(key);
      const rollback = () => {
        queryClient.setQueryData(key, prevData);
      };

      queryClient.setQueryData<Beneficiary[] | undefined>(key, (prev) => {
        if (prev) {
          return prev.filter((item) => item.id !== id);
        }
        return prev;
      });

      void message.loading({ content: t('deleting'), key });
      const successMessage = () => {
        void message.success({ content: t('deleted'), key, duration: 2 });
      };
      const errorMessage = (content: string) => {
        void message.error({ content, key, duration: 2 });
      };

      return {
        rollback,
        successMessage,
        errorMessage,
      };
    },
    onSuccess(res, newData, contenxt: any) {
      if (options?.onSuccess) void options.onSuccess(res, newData, contenxt);
      contenxt.successMessage();
      void queryClient.invalidateQueries(key);
    },
    onError(error: any, newData, contenxt) {
      if (options?.onError) void options.onError(error, newData, contenxt);
      void contenxt.errorMessage(t(error?.response?.data?.message) || 'Error!');
      contenxt.rollback();
    },
  });
};

const handleBody = (body: Beneficiary): Beneficiary => {
  if (body.type === 'BUSINESS') {
    const {
      address,
      country,
      document: { legalName },
    } = body;
    return {
      ...body,
      name: legalName,
      address: { country, ...address },
    };
  }
  const { country, address } = body;
  return { ...body, address: { country, ...address } };
};
